import { getSupabase } from '../supabase';
import { settingsFromRows, settingsToRows } from '../data/settings';
import type {
  AppSettings, CartLine, Category, Customer, Expense, ExpenseCategory, MenuItem,
  OpenTable, Order, Restaurant, RestaurantTable, StaffMember, StaffRole, TableKind,
} from '../types';
import type { Mutation } from './types';

/** Shape of a row in public.orders. */
interface OrderRow {
  id: string;
  order_no: number;
  table_id: string;
  customer_name: string;
  customer_phone: string;
  items: CartLine[];
  qty: number;
  subtotal: number | string;
  item_savings: number | string;
  discount_pct: number;
  discount_amount: number | string;
  total: number | string;
  placed_at: string;
  device_id: string;
}

const ms = (v: string | null | undefined): number | null =>
  v ? new Date(v).getTime() : null;

const num = (v: number | string): number => (typeof v === 'number' ? v : parseFloat(v));

export function rowToOrder(row: OrderRow): Order {
  const placed = new Date(row.placed_at);
  return {
    id: row.id,
    orderNo: row.order_no,
    // Once the server has numbered it, that number is the one staff quote.
    localNo: row.order_no,
    tableId: row.table_id,
    customer: { name: row.customer_name ?? '', phone: row.customer_phone ?? '' },
    items: row.items ?? [],
    qty: row.qty,
    subtotal: num(row.subtotal),
    itemSavings: num(row.item_savings),
    discountPct: row.discount_pct,
    discountAmount: num(row.discount_amount),
    total: num(row.total),
    placedAt: placed.getTime(),
    synced: true,
  };
}

/**
 * The restaurant every read and write is scoped to.
 *
 * Set once the membership is known and cleared on sign-out. Everything in this
 * module refuses to run without it: a write with no restaurant_id is rejected
 * by the schema, and a read without the filter would quietly return another
 * shop's data if RLS were ever misconfigured.
 */
let activeRestaurantId: string | null = null;

export function setActiveRestaurant(id: string | null): void {
  activeRestaurantId = id;
}

export function getActiveRestaurant(): string | null {
  return activeRestaurantId;
}

function requireRestaurant(): string {
  if (!activeRestaurantId) throw new Error('no restaurant selected');
  return activeRestaurantId;
}

/**
 * Pushes one queued change. Throws on failure so the caller can keep the entry
 * in the outbox and retry — never swallow, or a dropped order goes unnoticed.
 */
export async function pushMutation(m: Mutation, deviceId: string): Promise<void> {
  const sb = getSupabase();
  if (!sb) throw new Error('no backend configured');
  const rid = requireRestaurant();

  switch (m.kind) {
    case 'order': {
      const o = m.order;
      // Upsert on the client-generated uuid: retrying a request that actually
      // succeeded must not bill the customer twice.
      const { error } = await sb.from('orders').upsert(
        {
          restaurant_id: rid,
          id: o.id,
          table_id: o.tableId,
          customer_name: o.customer.name,
          customer_phone: o.customer.phone,
          items: o.items,
          qty: o.qty,
          subtotal: o.subtotal,
          item_savings: o.itemSavings,
          discount_pct: o.discountPct,
          discount_amount: o.discountAmount,
          total: o.total,
          placed_at: new Date(o.placedAt).toISOString(),
          device_id: deviceId,
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'table': {
      const { error } = await sb.from('open_tables').upsert(
        {
          restaurant_id: rid,
          table_id: m.tableId,
          cart: m.cart,
          discount: m.discount,
          customer_name: m.customer.name,
          customer_phone: m.customer.phone,
          opened_at: new Date(m.openedAt).toISOString(),
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'restaurant_id,table_id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'table_clear': {
      const { error } = await sb
        .from('open_tables')
        .delete()
        .eq('restaurant_id', rid)
        .eq('table_id', m.tableId);
      if (error) throw new Error(error.message);
      return;
    }

    case 'offer': {
      if (m.pct > 0) {
        const { error } = await sb
          .from('item_offers')
          .upsert(
            {
              restaurant_id: rid,
              item_id: m.itemId,
              pct: m.pct,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'item_id' },
          );
        if (error) throw new Error(error.message);
      } else {
        const { error } = await sb
          .from('item_offers')
          .delete()
          .eq('restaurant_id', rid)
          .eq('item_id', m.itemId);
        if (error) throw new Error(error.message);
      }
      return;
    }

    case 'custom_table': {
      const { error } = await sb
        .from('custom_tables')
        .upsert({ restaurant_id: rid, table_id: m.tableId }, { onConflict: 'table_id' });
      if (error) throw new Error(error.message);
      return;
    }

    case 'category': {
      const c = m.category;
      const { error } = await sb.from('categories').upsert(
        {
          restaurant_id: rid,
          id: c.id, name: c.name, name_hi: c.nameHi, name_gu: c.nameGu,
          icon: c.icon, sort_order: c.sortOrder, active: c.active,
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'category_delete': {
      const { error } = await sb.from('categories').delete().eq('id', m.id);
      if (error) throw new Error(error.message);
      return;
    }

    case 'menu_item': {
      const i = m.item;
      const { error } = await sb.from('menu_items').upsert(
        {
          restaurant_id: rid,
          id: i.id, category_id: i.cat, name: i.name,
          name_hi: i.nameHi, name_gu: i.nameGu, prices: i.prices,
          sort_order: i.sortOrder, active: i.active,
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'menu_item_delete': {
      const { error } = await sb.from('menu_items').delete().eq('id', m.id);
      if (error) throw new Error(error.message);
      return;
    }

    case 'restaurant_table': {
      const r = m.table;
      const { error } = await sb.from('restaurant_tables').upsert(
        {
          restaurant_id: rid,
          id: r.id, label: r.label, label_hi: r.labelHi, label_gu: r.labelGu,
          seats: r.seats, kind: r.kind, sort_order: r.sortOrder, active: r.active,
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'restaurant_table_delete': {
      const { error } = await sb.from('restaurant_tables').delete().eq('id', m.id);
      if (error) throw new Error(error.message);
      return;
    }

    case 'restaurant': {
      const r = m.restaurant;
      const { error } = await sb.from('restaurants').upsert(
        {
          id: r.id, name: r.name, address: r.address, phone: r.phone,
          gstin: r.gstin, active: r.active,
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'staff': {
      const u = m.member;
      const { error } = await sb.from('restaurant_users').upsert(
        {
          id: u.id,
          restaurant_id: u.restaurantId,
          email: u.email.trim().toLowerCase(),
          role: u.role,
          active: u.active,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    // Nothing queues this any more -- removing staff goes through the
    // manage-staff function so the login dies with the membership. Kept so a
    // tablet that was offline across the change still drains its outbox.
    case 'staff_delete': {
      const { error } = await sb.from('restaurant_users').delete().eq('id', m.id);
      if (error) throw new Error(error.message);
      return;
    }

    case 'expense': {
      const e = m.expense;
      // Upsert on the client-generated uuid, same reasoning as an order: a
      // retry after a dropped connection must not record the rent twice.
      const { error } = await sb.from('expenses').upsert(
        {
          restaurant_id: rid,
          id: e.id,
          category_id: e.categoryId,
          amount: e.amount,
          payee: e.payee,
          note: e.note,
          paid_at: new Date(e.paidAt).toISOString(),
          device_id: deviceId,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'expense_delete': {
      const { error } = await sb
        .from('expenses')
        .delete()
        .eq('restaurant_id', rid)
        .eq('id', m.id);
      if (error) throw new Error(error.message);
      return;
    }

    case 'expense_category': {
      const c = m.category;
      const { error } = await sb.from('expense_categories').upsert(
        {
          restaurant_id: rid,
          id: c.id, name: c.name, name_hi: c.nameHi, name_gu: c.nameGu,
          icon: c.icon, sort_order: c.sortOrder, active: c.active,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'id' },
      );
      if (error) throw new Error(error.message);
      return;
    }

    case 'expense_category_delete': {
      // The entries booked against it survive: the column is ON DELETE SET
      // NULL, so the spending stays on record as uncategorised.
      const { error } = await sb
        .from('expense_categories')
        .delete()
        .eq('restaurant_id', rid)
        .eq('id', m.id);
      if (error) throw new Error(error.message);
      return;
    }

    case 'settings': {
      // One row per key, written together. Upsert rather than update so a key
      // the database has never seen is created rather than silently dropped.
      const { error } = await sb.from('app_settings').upsert(
        settingsToRows(m.settings).map((r) => ({
          restaurant_id: rid,
          key: r.key,
          value: r.value,
          updated_at: new Date().toISOString(),
        })),
        { onConflict: 'restaurant_id,key' },
      );
      if (error) throw new Error(error.message);
      return;
    }
  }
}

export interface RemoteSnapshot {
  orders: Order[];
  openTables: Record<string, OpenTable>;
  offers: Record<string, number>;
  customTables: string[];
  /** Null when the menu tables are not deployed yet — keep the local menu. */
  categories: Category[] | null;
  menuItems: MenuItem[] | null;
  /** Null when restaurant_tables is absent or empty — keep the local plan. */
  restaurantTables: RestaurantTable[] | null;
  /** Null when app_settings is absent — keep the local settings. */
  settings: AppSettings | null;
  /** Every restaurant this user belongs to. */
  restaurants: Restaurant[];
  /** The roster for the active restaurant. */
  staff: StaffMember[];
  /**
   * Money out. Empty for a worker, and legitimately so: RLS refuses them the
   * rows rather than erroring, so there is nothing here to distinguish "not
   * allowed" from "nothing spent". The store decides which it is from the
   * role it already holds, and never merges these unless the user is an owner.
   */
  expenses: Expense[];
  expenseCategories: ExpenseCategory[];
}

/** Who the signed-in user is, and where they work. */
export interface Membership {
  restaurants: Restaurant[];
  /** Role per restaurant id. */
  roles: Record<string, StaffRole>;
  /** Runs the software: every restaurant, no till. */
  isPlatformAdmin: boolean;
}

/**
 * Loads the restaurants this user is a member of.
 *
 * Called before any other read: with no membership there is nothing to scope a
 * query to, and the app must show "no access" rather than an empty till that
 * looks like a fresh install.
 */
export async function fetchMembership(): Promise<Membership> {
  const sb = getSupabase();
  if (!sb) throw new Error('no backend configured');

  // An owner adds staff by email alone, so the membership row waits with a
  // null user_id until that person's account is matched to it. That used to
  // happen only in a trigger on sign-up, which stranded anyone who already had
  // an account when they were added. Claiming here instead means it is settled
  // on every sign-in, whatever order the two halves happened in.
  //
  // A failure is not fatal: someone whose membership is already claimed needs
  // nothing from this, and the read below is what actually decides access.
  const { error: claimErr } = await sb.rpc('claim_my_invites');
  if (claimErr) console.warn('claim_my_invites failed:', claimErr.message);

  const [shopsRes, meRes, adminRes] = await Promise.all([
    sb.from('restaurants').select('*').order('name'),
    sb.from('restaurant_users').select('restaurant_id, role, active'),
    sb.rpc('is_platform_admin'),
  ]);
  if (shopsRes.error) throw new Error(shopsRes.error.message);
  if (meRes.error) throw new Error(meRes.error.message);

  // Being an admin decides which screens exist, not which rows come back —
  // every policy above has already done the filtering. A failure here means
  // being treated as an ordinary member, which is the safe way to be wrong.
  if (adminRes.error) console.warn('is_platform_admin failed:', adminRes.error.message);
  const isPlatformAdmin = adminRes.data === true;

  const roles: Record<string, StaffRole> = {};
  // The select is filtered by RLS to this user's own rows, so the flag being
  // set on any of them is this user being asked to choose their own password.
  for (const r of meRes.data ?? []) {
    const row = r as {
      restaurant_id: string; role: string; active: boolean;
    };
    if (row.active !== false) {
      roles[row.restaurant_id] = row.role === 'owner' ? 'owner' : 'worker';
    }
  }

  const restaurants: Restaurant[] = (shopsRes.data ?? []).map((r) => {
    const row = r as {
      id: string; name: string; address: string; phone: string;
      gstin: string; active: boolean;
    };
    return {
      id: row.id,
      name: row.name,
      address: row.address ?? '',
      phone: row.phone ?? '',
      gstin: row.gstin ?? '',
      active: row.active !== false,
    };
  });

  return { restaurants, roles, isPlatformAdmin };
}

/**
 * Pulls what the other devices have. Orders are capped because a till does not
 * need three years of history in memory to take the next order.
 */
export async function pullSnapshot(
  orderLimit = 300,
  expenseLimit = 500,
): Promise<RemoteSnapshot> {
  const sb = getSupabase();
  if (!sb) throw new Error('no backend configured');
  const rid = requireRestaurant();

  // Every query is filtered by restaurant even though RLS already restricts
  // them: the filter is what makes a second shop's data impossible to fetch by
  // accident, and RLS is the backstop rather than the only guard.
  const mine = <T>(q: T): T => (q as any).eq('restaurant_id', rid);

  const [
    ordersRes, tablesRes, offersRes, customRes, catRes, itemRes, planRes, setRes,
    shopsRes, staffRes, expRes, expCatRes,
  ] = await Promise.all([
      mine(sb.from('orders').select('*')).order('placed_at', { ascending: false }).limit(orderLimit),
      mine(sb.from('open_tables').select('*')),
      mine(sb.from('item_offers').select('*')),
      mine(sb.from('custom_tables').select('*')),
      mine(sb.from('categories').select('*')),
      mine(sb.from('menu_items').select('*')),
      mine(sb.from('restaurant_tables').select('*')),
      mine(sb.from('app_settings').select('*')),
      sb.from('restaurants').select('*').order('name'),
      mine(sb.from('restaurant_users').select('*')),
      // Both are owner-only at the database. A worker gets an empty result,
      // not an error, so these sit with the rest rather than being skipped
      // client-side on a role the device may not have refreshed yet.
      mine(sb.from('expenses').select('*')).order('paid_at', { ascending: false }).limit(expenseLimit),
      mine(sb.from('expense_categories').select('*')),
    ]);

  for (const res of [ordersRes, tablesRes, offersRes, customRes]) {
    if (res.error) throw new Error(res.error.message);
  }

  const orders = (ordersRes.data ?? []).map((r) => rowToOrder(r as OrderRow)).reverse();

  const openTables: Record<string, OpenTable> = {};
  for (const r of tablesRes.data ?? []) {
    const row = r as {
      table_id: string; cart: CartLine[]; discount: number;
      customer_name: string; customer_phone: string; opened_at: string; updated_at: string;
    };
    openTables[row.table_id] = {
      tableId: row.table_id,
      cart: row.cart ?? [],
      discount: row.discount ?? 0,
      customer: { name: row.customer_name ?? '', phone: row.customer_phone ?? '' } as Customer,
      openedAt: new Date(row.opened_at).getTime(),
      updatedAt: new Date(row.updated_at).getTime(),
    };
  }

  const offers: Record<string, number> = {};
  for (const r of offersRes.data ?? []) {
    const row = r as { item_id: string; pct: number };
    offers[row.item_id] = row.pct;
  }

  const customTables = (customRes.data ?? []).map((r) => (r as { table_id: string }).table_id);

  // The menu tables may not be deployed yet (or may be empty before seeding).
  // Returning null tells the store to keep whatever menu it already has rather
  // than blanking the till.
  const categories: Category[] | null =
    catRes.error || !catRes.data?.length
      ? null
      : catRes.data.map((r) => {
          const row = r as {
            id: string; name: string; name_hi: string; name_gu: string;
            icon: string; sort_order: number; active: boolean;
          };
          return {
            id: row.id, name: row.name, nameHi: row.name_hi ?? '', nameGu: row.name_gu ?? '',
            icon: row.icon || 'grid', sortOrder: row.sort_order ?? 0, active: row.active !== false,
          };
        });

  const menuItems: MenuItem[] | null =
    itemRes.error || !itemRes.data?.length
      ? null
      : itemRes.data.map((r) => {
          const row = r as {
            id: string; category_id: string; name: string;
            name_hi: string; name_gu: string;
            prices: Record<string, number>; sort_order: number; active: boolean;
          };
          return {
            id: row.id, cat: row.category_id, name: row.name,
            nameHi: row.name_hi ?? '', nameGu: row.name_gu ?? '',
            prices: row.prices ?? {},
            sortOrder: row.sort_order ?? 0, active: row.active !== false,
          };
        });

  // The floor plan and the settings are the same story as the menu: a missing
  // or empty table means this migration has not been run yet, and blanking the
  // table picker would leave staff unable to take an order at all.
  const restaurantTables: RestaurantTable[] | null =
    planRes.error || !planRes.data?.length
      ? null
      : planRes.data.map((r) => {
          const row = r as {
            id: string; label: string; label_hi: string; label_gu: string;
            seats: number; kind: string; sort_order: number; active: boolean;
          };
          return {
            id: row.id,
            label: row.label ?? '',
            labelHi: row.label_hi ?? '',
            labelGu: row.label_gu ?? '',
            seats: row.seats ?? 0,
            kind: (row.kind === 'parcel' ? 'parcel' : 'dine_in') as TableKind,
            sortOrder: row.sort_order ?? 0,
            active: row.active !== false,
          };
        });

  const settings: AppSettings | null =
    setRes.error || !setRes.data?.length
      ? null
      : settingsFromRows(setRes.data as { key: string; value: unknown }[]);

  const restaurants: Restaurant[] = (shopsRes.data ?? []).map((r) => {
    const row = r as {
      id: string; name: string; address: string; phone: string;
      gstin: string; active: boolean;
    };
    return {
      id: row.id, name: row.name, address: row.address ?? '',
      phone: row.phone ?? '', gstin: row.gstin ?? '', active: row.active !== false,
    };
  });

  const staff: StaffMember[] = (staffRes.data ?? []).map((r) => {
    const row = r as {
      id: string; restaurant_id: string; user_id: string | null;
      email: string; role: string; active: boolean;
    };
    return {
      id: row.id,
      restaurantId: row.restaurant_id,
      userId: row.user_id,
      email: row.email,
      role: row.role === 'owner' ? 'owner' : 'worker',
      active: row.active !== false,
    };
  });

  // An error here is not fatal to the whole pull. This migration may not be
  // deployed yet on an older backend, and a till that refused to sync its
  // orders because the expenses table is missing would be a bad trade.
  if (expRes.error) console.warn('expenses read failed:', expRes.error.message);
  if (expCatRes.error) {
    console.warn('expense_categories read failed:', expCatRes.error.message);
  }

  const expenses: Expense[] = (expRes.data ?? []).map((r) => {
    const row = r as {
      id: string; category_id: string | null; amount: number | string;
      payee: string; note: string; paid_at: string;
    };
    return {
      id: row.id,
      categoryId: row.category_id,
      amount: num(row.amount),
      payee: row.payee ?? '',
      note: row.note ?? '',
      paidAt: new Date(row.paid_at).getTime(),
      synced: true,
    };
  });

  const expenseCategories: ExpenseCategory[] = (expCatRes.data ?? []).map((r) => {
    const row = r as {
      id: string; name: string; name_hi: string; name_gu: string;
      icon: string; sort_order: number; active: boolean;
    };
    return {
      id: row.id,
      name: row.name,
      nameHi: row.name_hi ?? '',
      nameGu: row.name_gu ?? '',
      icon: row.icon || 'wallet',
      sortOrder: row.sort_order ?? 0,
      active: row.active !== false,
    };
  });

  return {
    orders, openTables, offers, customTables, categories, menuItems,
    restaurantTables, settings, restaurants, staff, expenses, expenseCategories,
  };
}
